import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Receipt } from "lucide-react";
import { api, apiErrorMessage } from "../lib/api";
import { currencyTRY, dateTR, today } from "../lib/format";
import { GIDER_KATEGORI } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { Skeleton } from "../components/ui/skeleton";

export default function Expenses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    kategori: GIDER_KATEGORI[0],
    aciklama: "",
    tutar: 0,
    tarih: today(),
  });

  const load = async () => {
    try {
      const res = await api.get("/expenses");
      setItems(res.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses", { ...form, tutar: Number(form.tutar) });
      toast.success("Gider eklendi");
      setForm({
        kategori: GIDER_KATEGORI[0],
        aciklama: "",
        tutar: 0,
        tarih: today(),
      });
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu gideri silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Silindi");
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const total = items.reduce((s, x) => s + (x.tutar || 0), 0);

  const inputCls =
    "h-10 px-3 rounded-xl border border-stone-300 text-sm focus:border-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600/20";

  return (
    <div data-testid="expenses-page">
      <div className="mb-6">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
          Giderler
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900 mt-1">
          İşletme Giderleri
        </h1>
      </div>

      <form
        onSubmit={submit}
        className="rounded-2xl border border-stone-200 bg-white p-4 mb-6 grid grid-cols-1 md:grid-cols-6 gap-2"
      >
        <select
          value={form.kategori}
          onChange={(e) => setForm({ ...form, kategori: e.target.value })}
          className={inputCls}
          data-testid="gider-kategori"
        >
          {GIDER_KATEGORI.map((k) => (
            <option key={k}>{k}</option>
          ))}
        </select>
        <input
          placeholder="Açıklama"
          value={form.aciklama}
          onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
          className={inputCls + " md:col-span-2"}
          data-testid="gider-aciklama"
        />
        <input
          type="number"
          placeholder="Tutar (₺)"
          required
          value={form.tutar}
          onChange={(e) => setForm({ ...form, tutar: e.target.value })}
          className={inputCls}
          data-testid="gider-tutar"
        />
        <input
          type="date"
          required
          value={form.tarih}
          onChange={(e) => setForm({ ...form, tarih: e.target.value })}
          className={inputCls}
          data-testid="gider-tarih"
        />
        <button
          type="submit"
          data-testid="gider-ekle"
          className="h-10 rounded-xl bg-orange-700 hover:bg-orange-800 text-white font-semibold text-sm flex items-center justify-center gap-1"
        >
          <Plus className="w-4 h-4" /> Ekle
        </button>
      </form>

      <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between border-b border-stone-200 bg-stone-50/60">
          <span className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
            Tüm giderler
          </span>
          <span className="text-sm text-stone-700 num-tabular">
            Toplam:{" "}
            <b className="text-slate-900 font-display">{currencyTRY(total)}</b>
          </span>
        </div>
        {loading ? (
          <div className="p-6 space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-14 text-center">
            <Receipt className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <div className="font-display font-bold text-slate-900">
              Henüz gider yok
            </div>
            <div className="text-sm text-stone-500 mt-1">
              Yukarıdan yeni bir gider ekleyerek başlayın.
            </div>
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`gider-${it.id}`}
                className="px-5 py-3 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">
                    {it.kategori}
                  </div>
                  <div className="text-xs text-stone-500 truncate">
                    {it.aciklama || "—"} · {dateTR(it.tarih)}
                  </div>
                </div>
                <div className="font-display font-black text-slate-900 num-tabular">
                  {currencyTRY(it.tutar)}
                </div>
                {user?.role === "admin" && (
                  <button
                    onClick={() => remove(it.id)}
                    className="p-2 rounded-lg text-stone-400 hover:text-rose-700 hover:bg-rose-50"
                    data-testid={`gider-del-${it.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
